import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useEmploymentContractsSearchInline } from "@/queries/employment-contracts.query.ts";
import { useEmploymentTypesSearchInline } from "@/queries/employment-types.query.ts";
import {
  useOrganizationUpdateViewFilter,
  useOrganizationViewFilter,
} from "@/queries/settings/use-organization.query.ts";
import { useDepartmentsTree } from "@/queries/use-departments.query.ts";
import { useDivisionsByIds, useDivisionsSearchInline } from "@/queries/use-divisitions.query.ts";
import { useLocationsByIds, useLocationsSearchInline } from "@/queries/use-locations.query.ts";
import { useJobProfiles } from "@/queries/use-job-profiles.query.ts";
import { useJobRolesSearchInline } from "@/queries/use-job-roles.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiEmployeeLifecycleStatus,
  getLifecycleStatusSelectItems,
} from "@/types/employees/employee.types.ts";
import { ApiOrganizationSettingsViewFilter } from "@/types/organization.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Multiselect, Page, SelectItem, Stack, Title, useToasts } from "@skailer-com/uikit";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { z } from "zod";

export default function SettingsOrganizationViewFilter() {
  const { t } = useTranslation();
  const { showInfo } = useToasts();
  const { canCurrentEmployeeViewField, canIEdit, canCurrentEmployeeEditField } = usePermissions();
  const { data: viewFilter } = useOrganizationViewFilter();

  useTitle(t("core:organization_view_filter_title"), t("common:settings"));

  const schemaFields = z.object({
    job_profile_ids: z.array(z.number()),
    job_role_ids: z.array(z.number()),
    department_ids: z.array(z.number()),
    division_ids: z.array(z.number()),
    location_ids: z.array(z.number()),
    employee_lifecycle_statuses: z.array(z.enum(ApiEmployeeLifecycleStatus)),
    employment_type_ids: z.array(z.number()),
    employment_contract_ids: z.array(z.number()),
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof schemaFields>>({
    mode: "onSubmit",
    resolver: zodResolver(schemaFields),
    defaultValues: {
      job_profile_ids: [],
      job_role_ids: [],
      department_ids: [],
      division_ids: [],
      location_ids: [],
      employee_lifecycle_statuses: [],
      employment_type_ids: [],
      employment_contract_ids: [],
    },
  });

  useEffect(() => {
    if (viewFilter) {
      reset({
        job_profile_ids: viewFilter.job_profile_ids || [],
        job_role_ids: viewFilter.job_role_ids || [],
        department_ids: viewFilter.department_ids || [],
        division_ids: viewFilter.division_ids || [],
        location_ids: viewFilter.location_ids || [],
        employee_lifecycle_statuses: viewFilter.employee_lifecycle_statuses || [],
        employment_type_ids: viewFilter.employment_type_ids || [],
        employment_contract_ids: viewFilter.employment_contract_ids || [],
      });
    }
  }, [viewFilter]);

  const updateViewFilter = useOrganizationUpdateViewFilter(control);
  const onSubmit = handleSubmit(async (formData) => {
    if (!viewFilter) return;

    const payload: ApiOrganizationSettingsViewFilter = {
      job_profile_ids: formData.job_profile_ids,
      job_role_ids: formData.job_role_ids,
      department_ids: formData.department_ids,
      division_ids: formData.division_ids,
      location_ids: formData.location_ids,
      employee_lifecycle_statuses: formData.employee_lifecycle_statuses,
      employment_type_ids: formData.employment_type_ids,
      employment_contract_ids: formData.employment_contract_ids,
    };

    try {
      await updateViewFilter.mutateAsync(payload);

      showInfo({ title: t("common:you_changes_were_saved") });
      analyticsService.trackEvent(analyticEvents.organization.settingsEdited, {
        [analyticProperties.actionType]: "View Filter Edited",
        [analyticProperties.source]: "View Filter",
      });
    } catch {
      return;
    }
  });

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.organization.settingsViewed, {
      [analyticProperties.source]: "View Filter",
    });
  });

  const canViewJobProfiles = canCurrentEmployeeViewField(
    EmployeeFieldPermission.employee_position_job_title
  );
  const { options: jobProfileOptions, isLoading: isJobProfilesLoading } = useJobProfiles(
    false,
    canViewJobProfiles
  );
  const jobRoles = useJobRolesSearchInline(viewFilter?.job_role_ids || [], canViewJobProfiles);

  const canViewDepartments = canCurrentEmployeeViewField(
    EmployeeFieldPermission.employee_position_department
  );
  const departments = useDepartmentsTree(canViewDepartments);

  const canViewDivision = canCurrentEmployeeViewField(
    EmployeeFieldPermission.employee_position_division
  );
  const divisions = useDivisionsSearchInline(canViewDivision);
  const { data: divisionsLoaded } = useDivisionsByIds({
    ids: viewFilter?.division_ids || [],
    enabled: canViewDivision,
  });

  const canViewLocations = canCurrentEmployeeViewField(
    EmployeeFieldPermission.employee_position_location
  );
  const locations = useLocationsSearchInline(canViewLocations);
  const { data: locationsLoaded } = useLocationsByIds({
    ids: viewFilter?.location_ids || [],
    enabled: canViewLocations,
  });

  const canViewLifecycleStatus = canCurrentEmployeeViewField(
    EmployeeFieldPermission.employee_lifecycle_status
  );

  const canEditEmployment = canCurrentEmployeeEditField(
    EmployeeFieldPermission.manage_employee_employment
  );
  const canViewEmploymentTypes =
    canIEdit(ApiPermission.settings_employment_types) ||
    canEditEmployment ||
    canCurrentEmployeeViewField(EmployeeFieldPermission.employee_employment_employment_type);
  const employmentTypes = useEmploymentTypesSearchInline(canViewEmploymentTypes);

  const canViewEmploymentContracts =
    canIEdit(ApiPermission.settings_employment_contracts) ||
    canEditEmployment ||
    canCurrentEmployeeViewField(EmployeeFieldPermission.employee_employment_contract);
  const employmentContracts = useEmploymentContractsSearchInline(canViewEmploymentContracts);

  const renderFilterJobProfiles = () => {
    if (!canViewJobProfiles) return null;
    return (
      <Controller
        key={"job_profile_ids"}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:job_profile")}
            options={jobProfileOptions}
            values={value}
            onChange={onChange}
            searchable
            isClearable
            showCaption
            captionPerItem
            error={errors.job_profile_ids?.message}
            isLoading={isJobProfilesLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={"job_profile_ids"}
      />
    );
  };

  const renderFilterJobRoles = () => {
    if (!canViewJobProfiles) return null;
    return (
      <Controller
        key={"job_role_ids"}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:job_role")}
            options={jobRoles.options}
            searchOptions={jobRoles.options}
            onSearchOptions={jobRoles.search}
            loadOptionsOnOpen
            values={value}
            onChange={onChange}
            searchable
            isClearable
            error={errors.job_role_ids?.message}
            isLoading={jobRoles.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={"job_role_ids"}
      />
    );
  };

  const renderFilterDepartments = () => {
    if (!canViewDepartments) return null;
    return (
      <Controller
        key={"department_ids"}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:department")}
            options={departments.data}
            values={value}
            onChange={onChange}
            searchable
            isClearable
            error={errors.department_ids?.message}
            isLoading={departments.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={"department_ids"}
      />
    );
  };

  const renderFilterDivisions = () => {
    if (!canViewDivision) return null;

    return (
      <Controller
        key={"division_ids"}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:division")}
            options={divisionsLoaded as SelectItem[]}
            values={value}
            onChange={onChange}
            searchOptions={divisions.options}
            onSearchOptions={divisions.search}
            loadOptionsOnOpen
            searchable
            isClearable
            error={errors.division_ids?.message}
            isLoading={divisions.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={"division_ids"}
      />
    );
  };

  const renderFilterLocations = () => {
    if (!canViewLocations) return null;
    return (
      <Controller
        key={"location_ids"}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("common:location")}
            options={locationsLoaded}
            values={value}
            onChange={onChange}
            searchOptions={locations.options}
            onSearchOptions={locations.search}
            loadOptionsOnOpen
            searchable
            isClearable
            error={errors.location_ids?.message as string}
            isLoading={locations.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={"location_ids"}
      />
    );
  };

  const renderFilterEmployeeLifecycle = () => {
    if (!canViewLifecycleStatus) return null;
    return (
      <Controller
        key={"employee_lifecycle_statuses"}
        render={({ field: { value, onChange } }) => (
          <Multiselect
            label={t("core:lifecycle_status")}
            emptyTitle={t("common:select")}
            options={getLifecycleStatusSelectItems()}
            values={value}
            onChange={onChange}
            isClearable
            error={errors.employee_lifecycle_statuses?.message as string}
          />
        )}
        control={control}
        name={"employee_lifecycle_statuses"}
      />
    );
  };

  const renderFilterEmploymentTypes = () => {
    if (!canViewEmploymentTypes) return null;

    return (
      <Controller
        key={"employment_type_ids"}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:employment_type")}
            options={employmentTypes.options}
            values={value}
            onChange={onChange}
            searchOptions={employmentTypes.options}
            onSearchOptions={employmentTypes.search}
            loadOptionsOnOpen
            searchable
            isClearable
            error={errors.employment_type_ids?.message as string}
            isLoading={employmentTypes.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={"employment_type_ids"}
      />
    );
  };

  const renderFilterEmploymentContracts = () => {
    if (!canViewEmploymentContracts) return null;

    return (
      <Controller
        key={"employment_contract_ids"}
        render={({ field: { onChange, value } }) => (
          <Multiselect
            label={t("core:employment_contract")}
            options={employmentContracts.options}
            values={value}
            onChange={onChange}
            searchOptions={employmentContracts.options}
            onSearchOptions={employmentContracts.search}
            loadOptionsOnOpen
            searchable
            isClearable
            error={errors.employment_contract_ids?.message as string}
            isLoading={employmentContracts.isLoading}
            emptyTitle={t("common:select")}
          />
        )}
        control={control}
        name={"employment_contract_ids"}
      />
    );
  };

  return (
    <>
      <Page>
        <Page.Header
          title={t("core:organization_view_filter_title")}
          showBack={true}
          navigateTo={"/settings/general"}
        />
        <Page.Content>
          <Stack gap={"xl"} className={"container-sm mx-auto"}>
            <Title
              size={"lg"}
              paddingBottom
              paddingTop
              header={t("core:organization_view_filter_title")}
              caption={t("core:organization_view_filter_description")}
            />

            <form onSubmit={onSubmit}>
              {renderFilterJobProfiles()}
              {renderFilterJobRoles()}
              {renderFilterDivisions()}
              {renderFilterDepartments()}
              {renderFilterLocations()}
              {renderFilterEmployeeLifecycle()}
              {renderFilterEmploymentTypes()}
              {renderFilterEmploymentContracts()}
              <Stack gap={"sm"} className="mt-4">
                <Button size={"lg"} className={"w-full"} type={"submit"} onClick={onSubmit}>
                  {t("common:save")}
                </Button>
              </Stack>
            </form>
          </Stack>
        </Page.Content>
      </Page>
    </>
  );
}
